import { useEffect, useState, useMemo } from 'react';
import xhr from 'xhr';
import useAuth from 'hooks/useAuth';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Spinner,
  HStack,
  Input,
  FormLabel,
  VStack,
  Button,
  Alert,
  AlertIcon,
  Tfoot,
  Flex,
  FormControl,
  SimpleGrid,
} from '@chakra-ui/react';
import AiPagination from 'components/monitoring/ai/AiPagination';
import { BRAND, EmptyState, PageHeader, SectionCard } from '../components/ui';

const PAGE_SIZE = 20;

const FALLBACK_USD_TO_COP = 4186.71;

// Asesora de la sesión actual (antes estaba fija en el id 69).
const useCurrentAdviser = () => {
  const { user } = useAuth();
  return { adviserId: user?.adviser?.id };
};

const getUsdToCopRate = async (fallback = FALLBACK_USD_TO_COP) => {
  try {
    const resp = await fetch('https://economia.awesomeapi.com.br/json/last/USD-COP');
    if (!resp.ok) throw new Error('Error al obtener la tasa');
    const json = await resp.json();
    const rate = parseFloat(json?.USDCOP?.bid);
    if (!isNaN(rate)) return rate;
  } catch {
    return fallback;
  }
  return fallback;
};

const getCurrencyCode = (payment) => {
  const direct = payment?.currency?.data ?? payment?.currency;
  const viaAttributes = payment?.attributes?.currency?.data ?? payment?.attributes?.currency;
  const currencyWrapper = direct || viaAttributes || {};
  const attrs = currencyWrapper?.attributes || currencyWrapper || {};
  const code = (attrs.name || '').toString().toUpperCase();
  return code || 'COP';
};

export default function Commission() {
  const { adviserId: currentAdviserId } = useCurrentAdviser();

  const [payments, setPayments] = useState([]);
  const [displayPayments, setDisplayPayments] = useState([]);
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [usdToCopRate, setUsdToCopRate] = useState(FALLBACK_USD_TO_COP);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const rate = await getUsdToCopRate();
      if (mounted) setUsdToCopRate(rate);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!usdToCopRate || !currentAdviserId) return;
    fetchMonthlyPayments();
  }, [usdToCopRate, currentAdviserId]);

  const normalizePayment = (payment) => {
    const adviserRaw = payment?.adviser?.data ?? payment?.adviser ?? {};
    const adviser = adviserRaw?.attributes || adviserRaw;

    const user =
      adviser?.user?.data?.attributes ||
      adviser?.user ||
      (adviser?.attributes?.user?.data?.attributes ?? null);

    const fullName = user
      ? `${user.first_name || user.firstName || ''} ${user.last_name || user.lastName || ''}`.trim()
      : 'Sin nombre';

    const amountRaw = Number(payment?.amount ?? payment?.attributes?.amount ?? 0) || 0;
    const currencyCode = getCurrencyCode(payment);
    const amountCOP = currencyCode === 'USD' ? amountRaw * usdToCopRate : amountRaw;

    const commissionPercent = Number(adviser?.commission ?? 0) || 0;
    const commissionValue = (amountCOP * commissionPercent) / 100;

    const rawDate = payment?.payment_date || payment?.attributes?.payment_date || null;
    const dateObj = rawDate ? new Date(rawDate) : null;

    const studentCode = payment?.student?.data?.id ?? payment?.student?.id ?? '—';
    const paymentId = payment?.id ?? payment?.attributes?.id ?? '—';

    return {
      original: payment,
      paymentId,
      adviserName: fullName,
      amountCOP,
      commissionPercent,
      commissionValue,
      date: dateObj,
      studentCode,
      adviserId: payment?.adviser?.data?.id ?? payment?.adviser?.id ?? null,
    };
  };

  const commonPopulate =
    `&populate[adviser][populate][user]=true` +
    `&populate[student]=true` +
    `&populate[currency]=true` +
    `&pagination[limit]=-1`;

  const fetchMonthlyPayments = async () => {
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      endOfMonth.setHours(23, 59, 59, 999);

      const from = startOfMonth.toISOString().split('T')[0];
      const to = endOfMonth.toISOString().split('T')[0];

      const url =
        `/payments?filters[adviser][id][$eq]=${currentAdviserId}` +
        `&filters[state][$eq]=admisiones` +
        `&filters[payment_date][$gte]=${from}` +
        `&filters[payment_date][$lte]=${to}` +
        commonPopulate;

      const resp = await xhr(url);
      const raw = resp?.data?.data ?? [];
      const normalized = raw.map(normalizePayment);
      setPayments(normalized);
    } catch (e) {
      console.error('Error cargando pagos del mes:', e);
    }
  };

  const fetchPayments = async () => {
    try {
      if (!startDate || !endDate) {
        setError('Selecciona fechas válidas antes de buscar.');
        return;
      }
      if (!currentAdviserId) {
        setError('Tu usuario no tiene una asesora asociada.');
        return;
      }

      setLoading(true);
      setError('');

      const from = new Date(startDate).toISOString().split('T')[0];
      const to = new Date(endDate).toISOString().split('T')[0];

      const url =
        `/payments?filters[adviser][id][$eq]=${currentAdviserId}` +
        `&filters[state][$eq]=admisiones` +
        `&filters[payment_date][$gte]=${from}` +
        `&filters[payment_date][$lte]=${to}` +
        commonPopulate;

      const resp = await xhr(url);
      const rawPayments = resp?.data?.data ?? [];
      const normalized = rawPayments.map(normalizePayment);
      const sorted = normalized.sort((a, b) => (a.date?.getTime?.() || 0) - (b.date?.getTime?.() || 0));
      setDisplayPayments(sorted);
      setPage(1);
    } catch (e) {
      console.error('Error cargando pagos filtrados:', e);
      setError('Hubo un problema al cargar los pagos.');
    } finally {
      setLoading(false);
    }
  };

  const { totalAmount, totalCommission } = useMemo(() => {
    let amt = 0;
    let comm = 0;
    displayPayments.forEach((p) => {
      amt += p.amountCOP;
      comm += p.commissionValue;
    });
    return { totalAmount: amt, totalCommission: comm };
  }, [displayPayments]);

  const monthlySales = useMemo(() => payments.reduce((sum, p) => sum + p.amountCOP, 0), [payments]);
  const monthlyCommission = useMemo(() => payments.reduce((sum, p) => sum + p.commissionValue, 0), [payments]);

  const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);
  const currency = (value) => value.toLocaleString('es-CO', { style: 'currency', currency: 'COP' });

  const kpis = [
    { label: 'Mes actual', value: capitalize(new Date().toLocaleDateString('es-CO', { month: 'long', year: 'numeric' })) },
    { label: 'Ventas del mes', value: currency(monthlySales) },
    { label: 'Comisión del mes', value: currency(monthlyCommission) },
  ];

  return (
    <>
      <PageHeader title="Comisiones" subtitle='Pagos de "admisiones" asignados a ti y la comisión que generan.' />

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
        {kpis.map((kpi) => (
          <SectionCard key={kpi.label} padding={4}>
            <Text fontSize="sm" color="gray.500">
              {kpi.label}
            </Text>
            <Text fontSize="2xl" fontWeight="700" color="gray.800">
              {kpi.value}
            </Text>
          </SectionCard>
        ))}
      </SimpleGrid>

      <SectionCard mb={4} padding={4}>
        <Flex gap={4} align="flex-end" wrap="wrap">
          <FormControl maxW="220px">
            <FormLabel htmlFor="start-date" fontSize="sm">
              Desde
            </FormLabel>
            <Input type="date" id="start-date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </FormControl>
          <FormControl maxW="220px">
            <FormLabel htmlFor="end-date" fontSize="sm">
              Hasta
            </FormLabel>
            <Input type="date" id="end-date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </FormControl>
          <Flex gap={3}>
            <Button
              variant="ghost"
              onClick={() => {
                setStartDate('');
                setEndDate('');
                setDisplayPayments([]);
                setError('');
              }}
            >
              Limpiar
            </Button>
            <Button onClick={fetchPayments} bg={BRAND} color="white" _hover={{ bg: '#463fd1' }}>
              Buscar
            </Button>
          </Flex>
        </Flex>
        {error && (
          <Alert status="error" borderRadius="md" mt={4}>
            <AlertIcon />
            {error}
          </Alert>
        )}
      </SectionCard>

      <SectionCard padding={0} overflow="hidden">
        {loading ? (
          <Flex justify="center" py={12}>
            <Spinner color={BRAND} />
          </Flex>
        ) : displayPayments.length === 0 ? (
          <EmptyState title="Elige un rango de fechas" description="Verás cada pago del periodo con su comisión y los totales." />
        ) : (
          <Box overflowX="auto">
            <Table>
              <Thead bg="gray.50">
                <Tr>
                  <Th>#</Th>
                  <Th>ID pago</Th>
                  <Th>Fecha</Th>
                  <Th>Estudiante</Th>
                  <Th isNumeric>Valor</Th>
                  <Th isNumeric>Comisión</Th>
                </Tr>
              </Thead>
              <Tbody>
                {displayPayments.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((p, idx) => (
                  <Tr key={`${p.paymentId}-${idx}`}>
                    <Td>{(page - 1) * PAGE_SIZE + idx + 1}</Td>
                    <Td>{p.paymentId}</Td>
                    <Td>{p.date ? p.date.toLocaleDateString('es-CO') : '—'}</Td>
                    <Td>{p.studentCode}</Td>
                    <Td isNumeric>{currency(p.amountCOP)}</Td>
                    <Td isNumeric>{currency(p.commissionValue)}</Td>
                  </Tr>
                ))}
              </Tbody>
              <Tfoot>
                <Tr>
                  <Th colSpan={4} textAlign="right">
                    Totales
                  </Th>
                  <Th isNumeric>{currency(totalAmount)}</Th>
                  <Th isNumeric>{currency(totalCommission)}</Th>
                </Tr>
              </Tfoot>
            </Table>
            <AiPagination
              pagination={{
                page,
                pageSize: PAGE_SIZE,
                total: displayPayments.length,
                pageCount: Math.ceil(displayPayments.length / PAGE_SIZE),
              }}
              noun="pagos"
              extra="Los totales suman todos los pagos del periodo"
              onPageChange={setPage}
            />
          </Box>
        )}
      </SectionCard>
    </>
  );
}
